import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/seo/Seo';
import { websiteSchema, orgSchema, faqSchema } from '../components/seo/jsonld';
import GoogleAd from '../components/GoogleAd';
import { AD_SLOTS } from '../components/adSlots';

const tools = [
    {
        to: '/word-to-pdf',
        title: 'Word to PDF',
        description: 'Convert .docx documents to high-quality PDF files right in your browser.',
        icon: 'W',
    },
    {
        to: '/image-to-pdf',
        title: 'Image to PDF',
        description: 'Turn JPG, JPEG, or PNG images into print-ready PDFs fitted to A4.',
        icon: 'I',
    },
    {
        to: '/compress-pdf',
        title: 'Compress PDF',
        description: 'Shrink PDF file size for email and upload limits without losing quality.',
        icon: 'C',
    },
    {
        to: '/pdf-to-image',
        title: 'PDF to Image',
        description: 'Export each page of a PDF as a PNG image, delivered as a ZIP archive.',
        icon: 'P',
    },
    {
        to: '/pdf-to-word',
        title: 'PDF to Word',
        description: 'Extract editable text from a PDF into a Microsoft Word (.docx) document.',
        icon: 'T',
    },
];

const HOME_FAQ = [
    {
        q: 'Do you support files with passwords or DRM?',
        a: 'No. If a PDF is password-protected, remove the password in a desktop application (Adobe Acrobat, Apple Preview on Mac, etc.) before bringing it here. We do not support cracking encryption.',
    },
    {
        q: "What's the maximum file size?",
        a: 'There is no server limit because conversions happen on your own device. Most modern laptops handle PDFs up to a few hundred megabytes without problem.',
    },
    {
        q: 'Which browsers are supported?',
        a: 'Any reasonably recent version of Chrome, Firefox, Edge, Brave, or Safari. We rely on FileReader, Blob and WebAssembly APIs that have been stable in browsers for years.',
    },
    {
        q: 'Is this really free?',
        a: 'Yes. There are no accounts, no quotas, and no hidden upsells. Ads pay for the hosting and domain.',
    },
];

const HomePage = () => {
    return (
        <>
            <Seo
                title="Free PDF Converter — Word to PDF, Compress, PDF to Word"
                description="Convert Word to PDF, Image to PDF, compress PDFs, and extract text — all in your browser. Your files stay on your device. 100% free, private, and secure."
                path="/"
                jsonLd={[websiteSchema(), orgSchema(), faqSchema(HOME_FAQ)]}
            />

            <section className="hero-section">
                <div className="hero-inner">
                    <h1 className="hero-title">Secure Client-Side PDF Tools</h1>
                    <p className="hero-subtitle">
                        Convert, compress, and extract from PDFs — right in your browser.
                        <br />
                        No uploads. No waiting. 100% private.
                    </p>
                </div>
            </section>

            <div className="page-container">
                <section className="tools-grid-section">
                    <h2 className="section-title">Pick a Tool</h2>
                    <p className="section-lead">
                        Each tool runs entirely in your browser using WebAssembly and JavaScript.
                        We never receive, store, or even see your files.
                    </p>
                    <div className="tools-grid">
                        {tools.map((t) => (
                            <Link key={t.to} to={t.to} className="tool-card">
                                <span className="tool-card-icon" aria-hidden="true">{t.icon}</span>
                                <h3>{t.title}</h3>
                                <p>{t.description}</p>
                                <span className="tool-card-cta">Open tool &rarr;</span>
                            </Link>
                        ))}
                    </div>
                </section>

                <GoogleAd slot={AD_SLOTS.HOME_MID} className="ad-slot-inline" />

                <section className="content-section">
                    <h2>Why choose a browser-based PDF converter?</h2>
                    <p>
                        Most online PDF converters work by uploading your file to a remote server,
                        processing it there, and sending the result back. That convenience comes
                        with a real cost: your document — possibly a tax return, a contract, a
                        medical record, or a draft of work you haven't published — spends time on
                        infrastructure you don't control. Even the most reputable providers can be
                        breached, subpoenaed, or simply change their retention policies without
                        warning.
                    </p>
                    <p>
                        <strong>pdfconvertor.shop</strong> takes a fundamentally different
                        approach. Every tool on this site runs as JavaScript and WebAssembly code
                        inside your browser. When you click &quot;Convert,&quot; the heavy lifting
                        happens on your own CPU. The file never touches our servers — in fact,
                        we don't run servers for file processing at all.
                    </p>

                    <h3>What this means in practice</h3>
                    <ul>
                        <li>
                            <strong>Zero upload latency.</strong> A 50&nbsp;MB PDF that would take
                            a minute to upload and download elsewhere converts in seconds locally.
                        </li>
                        <li>
                            <strong>No retention risk.</strong> There is nothing for us to store,
                            log, or hand over. If a regulator or attacker asked us for a copy of
                            your file, we physically could not provide it.
                        </li>
                        <li>
                            <strong>Works on spotty connections.</strong> Once the page is
                            loaded, conversions continue to work even if your Wi-Fi drops.
                        </li>
                        <li>
                            <strong>Respectful of your data.</strong> We don't analyze, index,
                            or train on your documents.
                        </li>
                    </ul>

                    <h3>Is client-side conversion as good as server-side?</h3>
                    <p>
                        For most everyday use — Word to PDF, image to PDF, compression, extracting
                        text — yes. Browser engines are genuinely fast, and libraries like{' '}
                        <code>pdf-lib</code>, <code>pdf.js</code>, and <code>mammoth</code> are
                        mature enough to match typical online converter output. Very complex tasks
                        (OCR on scanned documents, editing locked PDFs, converting obscure legacy
                        formats) can still require server-side tooling, and we're transparent about
                        that in each tool's page.
                    </p>

                    <h3>How we keep the site free</h3>
                    <p>
                        This project is funded entirely by Google AdSense advertising. Ads appear
                        at well-defined places on the page and do not interfere with the tools
                        themselves. We don't sell data, we don't run affiliate schemes for products
                        we haven't used, and we don't have a paid tier. If the ads are ever
                        intrusive or misbehave, please let us know via the{' '}
                        <Link to="/contact">contact page</Link>.
                    </p>
                </section>

                <section className="content-section">
                    <h2>Frequently asked questions</h2>

                    <h3>Do you support files with passwords or DRM?</h3>
                    <p>
                        Currently, no. If a PDF is password-protected, you'll need to remove the
                        password in a desktop application (Adobe Acrobat, Apple Preview on Mac,
                        etc.) before bringing it here. We don't support cracking encryption, and
                        we won't — that's a clear line.
                    </p>

                    <h3>What's the maximum file size?</h3>
                    <p>
                        There is no server limit, but your own device matters. Most modern laptops
                        handle PDFs up to a few hundred megabytes without problem. If your browser
                        tab slows down, try converting a smaller file or closing other tabs.
                    </p>

                    <h3>Which browsers are supported?</h3>
                    <p>
                        Any reasonably recent version of Chrome, Firefox, Edge, Brave, or Safari.
                        We rely on modern APIs like <code>FileReader</code>, <code>Blob</code>,
                        and WebAssembly, which have been stable in browsers for years.
                    </p>

                    <h3>Is this really free?</h3>
                    <p>
                        Yes. There are no accounts, no quotas, and no hidden upsells. Ads pay for
                        the hosting and domain, and that's it.
                    </p>
                </section>
            </div>
        </>
    );
};

export default HomePage;
